import { PassEligibleRoom, PassUsageBookingSummary } from '../models/Pass';
import { spaceDetailPagePath } from './publicSpaceResolution';
import { roomBookingDetailsPath } from './roomBookingDetailsPath';
import { tutorBookingDetailsPath } from './tutorBookingDetailsPath';
import { tutorListingPath } from './searchPaths';
import { toUrlFriendly } from './urlUtils';

export type PassAudience = 'user' | 'tutor' | 'host';

/** Role-aware deep link for a pass usage event's booking, or null when no useful target exists. */
export function passUsageBookingPath(
  audience: PassAudience,
  booking: PassUsageBookingSummary,
): string | null {
  switch (audience) {
    case 'user':
      return booking.bookingType === 'TUTOR_BOOKING'
        ? `/user/tutor-bookings/${booking.bookingId}`
        : `/user/bookings/${booking.bookingId}`;
    case 'tutor':
      return booking.bookingType === 'TUTOR_BOOKING'
        ? tutorBookingDetailsPath('tutor', booking.bookingId)
        : null;
    case 'host':
      return booking.bookingType === 'TUTOR_BOOKING'
        ? tutorBookingDetailsPath('host', booking.bookingId)
        : roomBookingDetailsPath('host', booking.bookingId);
    default:
      return null;
  }
}

/** Role-aware link to an eligible tutor on a lesson-option pass. */
export function passEligibleTutorPath(audience: PassAudience, tutorId: string): string {
  if (audience === 'host') {
    return `/host/tutors/${tutorId}`;
  }
  return tutorListingPath(tutorId);
}

/**
 * Role-aware link to an eligible room on a space pass.
 * Public links need space city + name for the search URL slug.
 */
export function passEligibleRoomPath(
  audience: PassAudience,
  room: Pick<PassEligibleRoom, 'roomId' | 'name'>,
  space?: { name?: string | null; city?: string | null } | null,
): string | null {
  if (audience === 'host') {
    return `/host/rooms/${room.roomId}`;
  }
  if (audience === 'tutor') {
    return null;
  }
  const city = space?.city?.trim();
  const spaceName = space?.name?.trim();
  const roomName = room.name?.trim();
  if (!city || !spaceName || !roomName) {
    return null;
  }
  return `${spaceDetailPagePath(city, spaceName)}/${toUrlFriendly(roomName)}`;
}
